import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';
import ColumnValidationCard from './importDetails/ColumnValidationCard';
import ContentValidationCard from './importDetails/ContentValidationCard';
import DataTableCard from './importDetails/DataTableCard';
import DataComparisonCard from './importDetails/DataComparisonCard';
import './ImportDetails.css';

interface ImportDetails {
    id: number;
    state: string;
    filename: string;
    createdAt: string;
}

interface ImportContentResponse {
    format: string;
    data: Record<string, any>[];
    msg?: string;
}

interface RoundTable {
    id: number;
    name: string;
    number: number;
    iban: string;
    bic: string;
    kontoInhaber: string;
    bankName: string;
    billingName: string;
    billingEmail: string;
    billingPlz: string;
    billingCity: string;
    billingStreet: string;
    billingStreetNumber: string;
}

const ImportDetailsComponent: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    const [importData, setImportData] = useState<ImportDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [contentData, setContentData] = useState<Record<string, any>[]>([]);
    const [contentLoading, setContentLoading] = useState(true);
    const [contentError, setContentError] = useState<string | null>(null);
    const [roundTablesData, setRoundTablesData] = useState<RoundTable[]>([]);
    const [roundTablesLoading, setRoundTablesLoading] = useState(true);
    const [roundTablesError, setRoundTablesError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [originalStatus, setOriginalStatus] = useState<string>('');
    const [savingStatus, setSavingStatus] = useState(false);

    if (!authContext) {
        throw new Error('ImportDetailsComponent must be used within an AuthProvider');
    }
    if (!navigationContext) {
        throw new Error('ImportDetailsComponent must be used within a NavigationProvider');
    }

    const { token } = authContext;
    const { path } = navigationContext;

    // Extract ID from path like /imports/tischkonten/123
    const importId = path.split('/').pop();

    useEffect(() => {
        const fetchImportDetails = async () => {
            if (!importId || isNaN(Number(importId))) {
                setError('Ungültige Import-ID');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/roundtables/imports/${importId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('Import nicht gefunden');
                    }
                    throw new Error('Fehler beim Laden der Import-Details');
                }

                const data: ImportDetails = await response.json();
                setImportData(data);
                setSelectedStatus(data.state);
                setOriginalStatus(data.state);
                setError(null);
            } catch (error) {
                console.error('Error fetching import details:', error);
                setError(error instanceof Error ? error.message : 'Fehler beim Laden der Import-Details');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Import-Details');
            } finally {
                setLoading(false);
            }
        };

        fetchImportDetails();
    }, [importId, token]);

    useEffect(() => {
        const fetchImportContent = async () => {
            if (!importId || isNaN(Number(importId))) {
                setContentError('Ungültige Import-ID');
                setContentLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/roundtables/imports/${importId}/content`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('Import-Inhalt nicht gefunden');
                    }
                    throw new Error('Fehler beim Laden des Import-Inhalts');
                }

                const data: ImportContentResponse = await response.json();
                
                if (data.format === 'ERROR') {
                    throw new Error(data.msg || 'Fehler beim Laden des Import-Inhalts');
                }

                setContentData(data.data || []);
                setContentError(null);
            } catch (error) {
                console.error('Error fetching import content:', error);
                setContentError(error instanceof Error ? error.message : 'Fehler beim Laden des Import-Inhalts');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden des Import-Inhalts');
            } finally {
                setContentLoading(false);
            }
        };

        if (!loading && importData) {
            fetchImportContent();
        }
    }, [importId, token, loading, importData]);

    useEffect(() => {
        const fetchRoundTables = async () => {
            setRoundTablesLoading(true);
            try {
                const response = await fetch('/api/roundtables', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden der aktiven Daten');
                }

                const data: RoundTable[] = await response.json();
                setRoundTablesData(data);
                setRoundTablesError(null);
            } catch (error) {
                console.error('Error fetching round tables:', error);
                setRoundTablesError(error instanceof Error ? error.message : 'Fehler beim Laden der aktiven Daten');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der aktiven Daten');
            } finally {
                setRoundTablesLoading(false);
            }
        };

        fetchRoundTables();
    }, [token, refreshTrigger]);

    const handleDataUpdated = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedStatus(event.target.value);
    };

    const handleSaveStatus = async () => {
        if (!importId || savingStatus || selectedStatus === originalStatus) {
            return;
        }

        setSavingStatus(true);
        try {
            const response = await fetch(`/api/roundtables/imports/${importId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ state: selectedStatus }),
            });

            if (!response.ok) {
                throw new Error('Fehler beim Aktualisieren des Status');
            }

            const updatedData: ImportDetails = await response.json();
            setImportData(updatedData);
            setOriginalStatus(updatedData.state);
            setSelectedStatus(updatedData.state);
            toast.success('Status erfolgreich aktualisiert');
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Status');
        } finally {
            setSavingStatus(false);
        }
    };

    const handleDownload = async () => {
        if (!importId || downloading) {
            return;
        }

        setDownloading(true);
        try {
            const response = await fetch(`/api/roundtables/imports/${importId}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Herunterladen der Datei');
            }

            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = importData?.filename || `import-${importId}.xlsx`;
            
            if (contentDisposition) {
                const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
                if (matches && matches[1]) {
                    filename = matches[1];
                }
            }

            // Create blob from response and trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast.success('Download wird gestartet');
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Herunterladen der Datei');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return <div className="import-details-loading">Lade Import-Details...</div>;
    }

    if (error) {
        return (
            <div className="import-details-container">
                <div className="import-details-error">
                    <h2>Fehler</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!importData) {
        return (
            <div className="import-details-container">
                <div className="import-details-error">
                    <h2>Import nicht gefunden</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="import-details-container">
            <div className="import-details-header">
                <h2>Import Details</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                    <label style={{ fontWeight: 'bold' }}>Status:</label>
                    <select 
                        value={selectedStatus}
                        onChange={handleStatusChange}
                        style={{
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '1rem',
                        }}
                    >
                        <option value="new">Neu</option>
                        <option value="processing">In Bearbeitung</option>
                        <option value="completed">Abgeschlossen</option>
                        <option value="failed">Fehlgeschlagen</option>
                    </select>
                    {selectedStatus !== originalStatus && (
                        <button
                            onClick={handleSaveStatus}
                            disabled={savingStatus}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: savingStatus ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                opacity: savingStatus ? 0.6 : 1,
                            }}
                        >
                            {savingStatus ? 'Speichert...' : 'Status speichern'}
                        </button>
                    )}
                </div>
            </div>
            <div className="import-details-content">
                <div className="detail-row">
                    <span className="detail-label">Import ID:</span>
                    <span className="detail-value">{importData.id}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value">{importData.state}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Dateiname:</span>
                    <span className="detail-value">
                        {importData.filename}
                        <button 
                            className="download-btn"
                            onClick={handleDownload}
                            disabled={downloading}
                            title="Datei herunterladen"
                        >
                            {downloading ? '⏳' : '📥'}
                        </button>
                    </span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Erstellt am:</span>
                    <span className="detail-value">
                        {new Date(importData.createdAt).toLocaleString('de-DE')}
                    </span>
                </div>
            </div>

            {/* Column Validation Card */}
            <ColumnValidationCard data={contentData} />

            {/* Content Validation Card */}
            <ContentValidationCard data={contentData} />

            {/* Data Table Card */}
            {contentLoading ? (
                <div className="content-loading">Lade Daten...</div>
            ) : contentError ? (
                <div className="content-error">
                    <p>Fehler beim Laden der Daten: {contentError}</p>
                </div>
            ) : (
                <DataTableCard data={contentData} title="Daten aus dem Excel" rowsPerPage={10} />
            )}

            {/* Data Comparison Card */}
            {!contentLoading && !contentError && !roundTablesLoading && !roundTablesError && (
                <DataComparisonCard 
                    excelData={contentData}
                    roundTablesData={roundTablesData}
                    onDataUpdated={handleDataUpdated}
                />
            )}

            {/* Currently Active Data Table
            {roundTablesLoading ? (
                <div className="content-loading">Lade aktive Daten...</div>
            ) : roundTablesError ? (
                <div className="content-error">
                    <p>Fehler beim Laden der aktiven Daten: {roundTablesError}</p>
                </div>
            ) : (
                <DataTableCard 
                    data={roundTablesData.map(rt => ({
                        'ID': rt.id,
                        'Name': rt.name,
                        'Nummer': rt.number,
                        'IBAN': rt.iban,
                        'BIC': rt.bic,
                        'Kontoinhaber': rt.kontoInhaber,
                        'Bank': rt.bankName,
                        'Rechnungsname': rt.billingName,
                        'Rechnungs-E-Mail': rt.billingEmail,
                        'PLZ': rt.billingPlz,
                        'Stadt': rt.billingCity,
                        'Straße': rt.billingStreet,
                        'Hausnummer': rt.billingStreetNumber
                    }))} 
                    title="Aktuell Aktive Daten" 
                />
            )} */}
        </div>
    );
};

export default ImportDetailsComponent;
